import {
    DASHED_LINE,
    INDENT_DASHED_LINE,
    CUT_COLOR,
    DRAW_EDGE_INDENT,
    DROP_COLOR,
    HATCH_COLOR_CUT_OFF,
    HATCH_COLOR_WASTE,
    HATCHING_STEP_CUT_OFF,
    HATCHING_STEP_WASTE,
    JAVA_CANVAS_HEIGHT,
    JAVA_CANVAS_HEIGHT_VERTICAL,
    JAVA_CANVAS_WIDTH,
    JAVA_CANVAS_WIDTH_VERTICAL,
    MIN_DRAW_WASTE,
    START_INDENT,
    TURN_COLORS,
} from './constants';
import {buildHatchLines} from './hatching';
import {fitLabelInRect} from './textFit';
import {javaIntCoord, javaIntSize, javaSizeLabel} from './javaNumbers';

/**
 * Walk CuttingMap tree like DrawMapUtil and collect drawable primitives.
 *
 * @param {object} cuttingMap root CuttingMap JSON
 * @param {{drawEdge?: boolean, drawPosition?: boolean}} [options]
 * @returns {object} scene primitives for Three.js / React
 */
export function buildCuttingMapScene(cuttingMap, options = {}) {
    const {drawEdge = true, drawPosition = true} = options;
    const trimming = cuttingMap?.trimming ?? [0, 0, 0, 0];
    const originX = START_INDENT + trimming[0];
    const originY = START_INDENT + trimming[2];

    const scene = {
        sheet: {
            length: Number(cuttingMap.length),
            width: Number(cuttingMap.width),
            trimming,
            startIndent: START_INDENT,
            structure: cuttingMap.structure,
        },
        details: [],
        edges: [],
        labels: [],
        cuts: [],
        cutClears: [],
        cutOffs: [],
        wastes: [],
    };

    walkMap(cuttingMap, originX, originY, drawEdge, drawPosition, scene);
    return scene;
}

function walkMap(cuttingMap, originX, originY, drawEdge, drawPosition, scene) {
    if (!cuttingMap) {
        return;
    }

    const children = cuttingMap.cuttingMaps;

    // Java: if (cuttingMaps == null) → only cutOff / waste
    if (children == null) {
        collectCutOff(cuttingMap.cutOff, originX, originY, scene);
        collectWaste(cuttingMap.waste, originX, originY, scene);
        return;
    }

    collectDetailAndChildren(cuttingMap, children, originX, originY, drawEdge, drawPosition, scene);

    collectCutOff(cuttingMap.cutOff, originX, originY, scene);
    collectWaste(cuttingMap.waste, originX, originY, scene);
    collectCut(cuttingMap.cut0, originX, originY, scene);
    collectCut(cuttingMap.cut1, originX, originY, scene);
}

function collectDetailAndChildren(cuttingMap, children, originX, originY, drawEdge, drawPosition, scene) {
    const detail = cuttingMap.detail;
    if (!detail) {
        return;
    }

    const points = cuttingMap.points;
    if (!points?.length) {
        return;
    }

    const x0 = javaIntCoord(points[0].x);
    const y0 = javaIntCoord(points[0].y);

    let a = javaIntSize(detail.length);
    let b = javaIntSize(detail.width);
    let hasL1 = detail.l1 != null;
    let hasL2 = detail.l2 != null;
    let hasW1 = detail.w1 != null;
    let hasW2 = detail.w2 != null;

    if (cuttingMap.detailOrientation === 'VERTICAL') {
        a = javaIntSize(detail.width);
        b = javaIntSize(detail.length);
        hasL1 = detail.w1 != null;
        hasL2 = detail.w2 != null;
        hasW1 = detail.l2 != null;
        hasW2 = detail.l1 != null;
    }

    const absX = originX + x0;
    const absY = originY + y0;
    const turns = Number(cuttingMap.relativeNumberTurns) || 0;
    const position = drawPosition ? `${detail.position || ''} ` : '';
    const detailDrop = position.toLowerCase().includes('drop');
    const color = detailDrop ? DROP_COLOR : (TURN_COLORS[turns] ?? TURN_COLORS[3]);

    const sizeText = `${javaSizeLabel(detail.length)}x${javaSizeLabel(detail.width)}`;
    let text = `${position}${sizeText}`;
    if (detail.detailReserve) {
        text = `[ ${text} ]`;
    }
    const {fontSize, rotate} = fitLabelInRect(text, a, b);

    scene.details.push({
        x: absX,
        y: absY,
        width: a,
        height: b,
        color,
        position: detail.position,
        label: {
            text,
            fontSize,
            rotate,
        },
    });

    if (drawEdge) {
        collectEdges(absX, absY, a, b, hasL1, hasL2, hasW1, hasW2, scene);
    }

    // Java always recurses into children[0] and children[1] when detail != null
    walkMap(children[0], originX, originY, drawEdge, drawPosition, scene);
    walkMap(children[1], originX, originY, drawEdge, drawPosition, scene);
}

function collectEdges(x, y, a, b, l1, l2, w1, w2, scene) {
    const i = DRAW_EDGE_INDENT;
    if (l1) {
        scene.edges.push({from: [x + i, y + i], to: [x + a - i, y + i]});
    }
    if (l2) {
        scene.edges.push({from: [x + i, y + b - i], to: [x + a - i, y + b - i]});
    }
    if (w1) {
        scene.edges.push({from: [x + i, y + i], to: [x + i, y + b - i]});
    }
    if (w2) {
        scene.edges.push({from: [x + a - i, y + i], to: [x + a - i, y + b - i]});
    }
}

function collectCut(cut, originX, originY, scene) {
    if (!cut?.point0 || !cut?.point1) {
        return;
    }

    // Mirror Java: red dash segments only; gaps are transparent over the sheet (white kerf).
    // On vector UI thick detail outlines can bleed into the kerf — clear the full cut with white first.
    const stroke = 2;
    const startX = originX + javaIntCoord(cut.point0.x);
    const startY = originY + javaIntCoord(cut.point0.y);
    const xEnd = originX + javaIntCoord(cut.point1.x);
    const yEnd = originY + javaIntCoord(cut.point1.y);

    scene.cutClears.push({
        from: [startX, startY],
        to: [xEnd, yEnd],
        color: '#FFFFFF',
    });

    const segments = [];
    let x0 = startX;
    let y0 = startY;

    if (cut.orientation === 'HORIZONTAL') {
        const span = javaIntCoord(cut.point1.x) - javaIntCoord(cut.point0.x);
        const count = Math.floor(span / (DASHED_LINE + INDENT_DASHED_LINE));
        let x1 = x0 + DASHED_LINE;
        for (let i = 0; i < count; i++) {
            segments.push({from: [x0, y0], to: [x1 - stroke, y0]});
            x0 += DASHED_LINE + INDENT_DASHED_LINE;
            x1 += DASHED_LINE + INDENT_DASHED_LINE;
        }
        if (xEnd - x0 > DASHED_LINE) {
            segments.push({from: [x0, y0], to: [x0 + DASHED_LINE - stroke, y0]});
        } else {
            segments.push({from: [x0, y0], to: [xEnd, y0]});
        }
    } else {
        const span = javaIntCoord(cut.point1.y) - javaIntCoord(cut.point0.y);
        const count = Math.floor(span / (DASHED_LINE + INDENT_DASHED_LINE));
        let y1 = y0 + DASHED_LINE;
        for (let i = 0; i < count; i++) {
            segments.push({from: [x0, y0], to: [x0, y1 - stroke]});
            y0 += DASHED_LINE + INDENT_DASHED_LINE;
            y1 += DASHED_LINE + INDENT_DASHED_LINE;
        }
        if (yEnd - y0 > DASHED_LINE) {
            segments.push({from: [x0, y0], to: [x0, y0 + DASHED_LINE - stroke]});
        } else {
            segments.push({from: [x0, y0], to: [x0, yEnd]});
        }
    }

    scene.cuts.push({
        id: scene.cuts.length,
        from: [startX, startY],
        to: [xEnd, yEnd],
        orientation: cut.orientation,
        lengthCut: cut.lengthCut,
        widthCut: cut.widthCut,
        color: CUT_COLOR,
        segments,
    });
}

function collectCutOff(cutOff, originX, originY, scene) {
    if (!cutOff?.points?.length) {
        return;
    }
    const w = javaIntSize(cutOff.length);
    const h = javaIntSize(cutOff.width);
    const x = originX + javaIntCoord(cutOff.points[0].x);
    const y = originY + javaIntCoord(cutOff.points[0].y);
    const text = `${w}x${h}`;
    const {fontSize, rotate} = fitLabelInRect(text, w, h);
    scene.cutOffs.push({
        x,
        y,
        width: w,
        height: h,
        hatchLines: buildHatchLines(x, y, w, h, HATCHING_STEP_CUT_OFF, false),
        hatchColor: HATCH_COLOR_CUT_OFF,
        label: {text, fontSize, rotate},
    });
}

function collectWaste(waste, originX, originY, scene) {
    if (!waste?.points?.length) {
        return;
    }
    const w = javaIntSize(waste.length);
    const h = javaIntSize(waste.width);
    if (w < MIN_DRAW_WASTE || h < MIN_DRAW_WASTE) {
        return;
    }
    const x = originX + javaIntCoord(waste.points[0].x);
    const y = originY + javaIntCoord(waste.points[0].y);
    scene.wastes.push({
        x,
        y,
        width: w,
        height: h,
        hatchLines: buildHatchLines(x, y, w, h, HATCHING_STEP_WASTE, true),
        hatchColor: HATCH_COLOR_WASTE,
    });
}

/**
 * Scene size for the orthographic camera.
 * Baseline matches Java BufferedImage (2828×2092); if the sheet in mm is larger
 * (1 mm = 1 world unit), expand so the frame and details stay inside the view.
 */
export function getSceneBounds(scene) {
    const sheet = scene?.sheet ?? {};
    const structure = sheet.structure;
    const vertical = structure === 'VERTICAL';
    const minWidth = vertical ? JAVA_CANVAS_WIDTH_VERTICAL : JAVA_CANVAS_WIDTH;
    const minHeight = vertical ? JAVA_CANVAS_HEIGHT_VERTICAL : JAVA_CANVAS_HEIGHT;

    const trimming = sheet.trimming ?? [0, 0, 0, 0];
    const startIndent = Number(sheet.startIndent) || START_INDENT;
    const [t0 = 0, t1 = 0, t2 = 0, t3 = 0] = trimming;
    const length = Number(sheet.length) || 0;
    const width = Number(sheet.width) || 0;
    // Same extents as SheetOutline: indent + outer (trimming + usable) + trailing indent
    const contentWidth = startIndent + t0 + length + t1 + startIndent;
    const contentHeight = startIndent + t2 + width + t3 + startIndent;

    return {
        totalWidth: Math.max(minWidth, contentWidth),
        totalHeight: Math.max(minHeight, contentHeight),
    };
}
